/**
 * Anonymous, privacy-first event tracking for ToneGuard.
 *
 * - Persists a random UUID in `localStorage["tg_user_id"]` so repeat visits
 *   on the same device are tied to the same anonymous id.
 * - Sends behavioral events to the backend via `fetch` with `keepalive: true`
 *   so the request survives page unload and never blocks UI interactions.
 * - Silently swallows every failure. Analytics MUST NOT affect the user
 *   experience if the network or backend is down.
 * - NEVER sends raw message content. Metadata is restricted to the keys
 *   whitelisted below (intent, style, risk_level, variant).
 */

const USER_KEY = "tg_user_id";
const PRIVACY_KEY = "toneguard:privacyMode";
const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`;

// Static import so our dual-emit always has a live PostHog reference.
// phCapture is null-safe: it's a silent no-op until initPostHog runs.
import { phCapture } from "@/lib/posthogClient";

const VALID_METADATA_KEYS = new Set([
    "intent",
    "style",
    "risk_level",
    "variant",
]);

const VALID_EVENTS = new Set([
    "session_started",
    "message_reviewed",
    "message_copied",
    "message_refined",
    "intent_selected",
]);

function readUserId() {
    if (typeof window === "undefined" || !window.localStorage) return null;
    try {
        let id = window.localStorage.getItem(USER_KEY);
        if (id && typeof id === "string" && id.length >= 8) return id;
        if (typeof window.crypto?.randomUUID === "function") {
            id = window.crypto.randomUUID();
        } else {
            // Extremely old browser fallback — Math.random-based UUIDv4.
            id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        }
        window.localStorage.setItem(USER_KEY, id);
        return id;
    } catch {
        return null;
    }
}

function sanitizeMetadata(meta) {
    if (!meta || typeof meta !== "object") return null;
    const out = {};
    for (const [k, v] of Object.entries(meta)) {
        if (!VALID_METADATA_KEYS.has(k)) continue;
        // Only allow short primitive strings/numbers — never raw message text.
        if (typeof v === "string" && v.length <= 64) out[k] = v;
        else if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : null;
}

/**
 * Fire-and-forget event tracker. Safe to call from render paths.
 * Returns nothing; callers never need to await.
 *
 * Dual-emit: every event is also mirrored to PostHog (when configured)
 * so product analytics see a single consistent identity per device.
 */
export function track(event, metadata) {
    try {
        if (!VALID_EVENTS.has(event)) return;
        const userId = readUserId();
        if (!userId) return;
        const cleanMeta = sanitizeMetadata(metadata);
        // Privacy Mode (set from the Profile panel) is a hard local kill-
        // switch: nothing leaves the browser. Local counters still fire
        // below this function so ToneMirror's on-device insights work.
        if (isPrivacyModeOn()) return;
        const body = JSON.stringify({
            user_id: userId,
            event,
            timestamp: new Date().toISOString(),
            metadata: cleanMeta,
        });
        // keepalive lets this request survive tab-close / navigation.
        fetch(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        }).catch(() => {
            /* silent */
        });
        // Mirror to PostHog — static import, silent no-op before init.
        try {
            phCapture(event, cleanMeta || undefined);
        } catch {
            /* silent */
        }
    } catch {
        /* silent */
    }
}

function isPrivacyModeOn() {
    try {
        return window.localStorage.getItem(PRIVACY_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * Sync the privacy mode flag from app state. Called by ToneGuardPage
 * whenever the user toggles "Privacy Mode" in the Profile panel.
 */
export function setPrivacyMode(on) {
    try {
        if (on) window.localStorage.setItem(PRIVACY_KEY, "1");
        else window.localStorage.removeItem(PRIVACY_KEY);
    } catch {
        /* silent */
    }
}

let sessionFired = false;

/**
 * Call once per page load (e.g. from a top-level effect). Additional
 * invocations in the same tab are no-ops.
 */
export function trackSessionStart() {
    if (sessionFired) return;
    sessionFired = true;
    track("session_started");
}

export function getAnonUserId() {
    return readUserId();
}

// ----- Local counters for insight generation -----
// We also increment a tiny local counter alongside any network event so the
// ToneMirror dashboard can compute behavioral ratios (e.g. copy / review)
// without a round-trip to the server. Counters are additive only, reset
// alongside the chat history via the "Clear" button.
const COUNTERS_KEY = "toneguard:counters";

export function bumpLocalCounter(name) {
    try {
        const raw = window.localStorage.getItem(COUNTERS_KEY);
        const c = raw ? JSON.parse(raw) : {};
        c[name] = (c[name] || 0) + 1;
        window.localStorage.setItem(COUNTERS_KEY, JSON.stringify(c));
    } catch {
        /* silent */
    }
}

export function readLocalCounters() {
    try {
        const raw = window.localStorage.getItem(COUNTERS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}
